<template>
    <Comp :language="language" :channel-configs="channelConfigs" @handle-share="handleShare" />
</template>

<script >
import { isMoomoo } from '@futuweb/tool-browser';
import NNQ from '@futuweb/tool-nn/dist/nnq';
import copy from 'copy-to-clipboard';
import { computed, onMounted, ref, watch } from 'vue-demi';
// 引入常量配置
import { EXPOSE_FEGO_METHOD, SHARE_CHANNELS, SHARE_TYPE, ShareEventMarket, BEHAVIOR_EVENT, CHANNEL_MAP } from '../common/constants';
// 引入裂变分享链接生成工具
import { getFissionShareUrl } from '../common/fission';
import { languageMap } from '../common/lang';
import { EVENT_NAME, ftReport, EVENT_VERSION } from '../common/report';
import request from '../common/request';
import { getEnableChannel, getShareOptions } from '../common/share';
import { replaceTemplateString } from '../common/util';
import Comp from '../components/shareComp.vue';
import { Language } from '../type';
const __sfc_main = {};
__sfc_main.props = {
  // 语种
  language: {
    type: String,
    default: Language.EN
  },
  market: {
    type: String,
    default: ShareEventMarket.SG
  },
  pageName: {
    type: String,
    default: ''
  },
  isNeedShortLink: {
    type: Boolean,
    default: true
  },
  isFissionShare: {
    type: Boolean,
    default: true
  },
  fissionShareConfig: {
    type: Object,
    default: () => ({
      defaultPromoteId: '',
      isBindCommonInvite: true,
      isGetPromoteIdFromUrl: false
    })
  },
  shareType: {
    type: String,
    default: SHARE_TYPE.LINK
  },
  shareConfigs: {
    type: Object,
    default: () => ({
      shareDataVar: '',
      shareDefaultLink: '',
      shareTitle: '',
      shareDescription: '',
      shareImageUrl: '',
      // 分享图标url
      shareCopyContent: ''
    })
  },
  channelConfigs: {
    type: Array,
    default: () => []
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const {
    common: {
      env: {
        // 预渲染
        isPreRender
      }
    },
    store: {
      variable
    }
  } = require('@futuweb/fego-render');
  const {
    useBehaviour,
    useToast
  } = require('@fegobull/webapi').use;
  const {
    runBehaviour
  } = useBehaviour();
  const Toast = useToast();
  const uid = ref(variable.data.UID || 0);
  const shareOptins = ref({});
  const shareUrl = ref('');
  const hasInstalledApp = ref([]);
  const hasBuiltShortUrl = ref(false);
  const isInitBridgeSuccess = ref(false);
  const props = __props;
  const emits = __ctx.emit;

  // 模拟多语言对象
  const lang = computed(() => ({
    ...languageMap[props.language]
  }));
  const shareDataValue = computed(() => {
    if (!props.shareConfigs.shareDataVar) {
      // 如果分享链接变量不存在，则使用分享默认链接
      return props.shareType === SHARE_TYPE.LINK ? props.shareConfigs.shareDefaultLink : '';
    }
    return variable.data[props.shareConfigs.shareDataVar] || '';
  });
  const getShareCopyContent = computed(() => {
    const obj = {
      shareUrl: shareUrl.value
    };
    return replaceTemplateString(props.shareConfigs.shareCopyContent, obj);
  });
  const getShortUrl = async (urls, retry = 1) => {
    try {
      const res = await request.getShortLink({
        links: urls
      });
      if (Array.isArray(res.shortUrls) && res.shortUrls.length > 0) {
        hasBuiltShortUrl.value = true;
        return res.shortUrls;
      }
      // 请求成功但返回数据无效，需要重试
      return [];
    } catch (error) {
      hasBuiltShortUrl.value = false;
      // 请求报错，如果还有重试次数，则进行重试
      if (retry > 0) {
        return await getShortUrl(urls, retry - 1);
      }
      return [];
    }
  };

  // 获取分享数据
  const getShareData = async () => {
    let shareData = '';

    // 链接分享
    if (props.shareType === SHARE_TYPE.LINK) {
      shareData = shareDataValue.value || props.shareConfigs.shareDefaultLink || location.href;
      if (props.isFissionShare) {
        shareData = getFissionShareUrl(shareData, props.fissionShareConfig, uid.value);
      }
      if (!shareData) {
        return '';
      }
      if (props.isNeedShortLink) {
        const shortUrls = await getShortUrl([shareData]);
        shareData = shortUrls[0] || shareData;
      }
    }
    shareUrl.value = shareData;
    return shareData;
  };

  // 如果配置了分享裂变与生成短链，则需要登录
  const checkLogin = () => {
    if (props.isFissionShare || props.isNeedShortLink) {
      if (!uid.value) {
        runBehaviour(null, {
          type: 'login',
          value: location.href,
          withQuery: true
        });
        return false; // 返回 false 表示未登录
      }
    }
    return true; // 返回 true 表示已登录或不需要登录
  };
  const initShareOptions = async () => {
    // 检查登录状态，如果未登录则终止初始化
    if (!checkLogin()) {
      return;
    }
    const shareData = await getShareData();
    if (!shareData) {
      return;
    }
    const shareConfig = {
      title: props.shareConfigs.shareTitle,
      description: props.shareConfigs.shareDescription,
      imgUrl: props.shareConfigs.shareImageUrl,
      link: shareData,
      images: shareData
    };
    shareOptins.value = getShareOptions(shareConfig, props.shareType);
  };
  const initBridge = async () => {
    return new Promise((resolve, reject) => {
      try {
        NNQ.initBridge({}, () => {
          resolve(true);
        });
      } catch (error) {
        reject(false);
      }
    });
  };

  // 渠道点击处理
  const handleShare = async channelName => {
    try {
      // 点击分享按钮上报
      ftReport(EVENT_NAME.CLICK_SHARE_BTN, {
        page_name: props.pageName,
        channel_name: channelName,
        share_type: props.shareType
      });

      // 检查登录状态，如果未登录则终止分享流程
      if (!checkLogin()) {
        return;
      }
      if (!hasBuiltShortUrl.value) {
        // 如果短链未生成，则重新初始化分享选项
        await initShareOptions();
      }
      // 兜底复制内容
      copy(getShareCopyContent.value);
      // 复制
      if (channelName === SHARE_CHANNELS.COPY) {
        Toast.success({
          message: lang.value.copySuccess,
          zIndex: 9999
        });
        return;
      }
      // 不是moomoo，则直接返回
      if (!isMoomoo()) {
        return;
      }
      // 如果isInitBridgeSuccess为false，则直接返回
      if (!isInitBridgeSuccess.value) {
        isInitBridgeSuccess.value = await initBridge();
      }

      // 不是系统分享，校验是否安装了对应的app
      if (channelName !== SHARE_CHANNELS.SYSTEM) {
        if (!hasInstalledApp.value.includes(channelName)) {
          Toast.info({
            message: lang.value.appNotInstalled,
            zIndex: 9999
          });
          return;
        }
      }
      const options = {
        enable_channel: channelName,
        disable_channel: [],
        ...shareOptins.value
      };

      // 站内结构化分享
      NNQ.showDiverseShare(options, ({
        channel
      }) => {
        ftReport(EVENT_NAME.SHARE, {
          page_name: props.pageName,
          channel_name: CHANNEL_MAP[channel],
          share_type: props.shareType
        }, EVENT_VERSION.SHARE);
        emits(BEHAVIOR_EVENT.CHANNEL_SHARE_SUCCESS);
      }, err => {
        console.log('showDiverseShare error', err);
        Toast.error({
          message: lang.value.requestFail,
          zIndex: 9999
        });
      });
    } catch (error) {
      Toast.error({
        message: lang.value.requestFail,
        zIndex: 9999
      });
    }
  };
  watch(() => shareDataValue.value, () => {
    initShareOptions();
  });
  onMounted(async () => {
    if (isPreRender) {
      return;
    }
    hasBuiltShortUrl.value = !props.isNeedShortLink;
    initShareOptions();
    if (isMoomoo()) {
      // 初始化jsbridge
      isInitBridgeSuccess.value = await initBridge();
      hasInstalledApp.value = await getEnableChannel();
    }
  });
  // 暴露方法至fego调用
  return Object.assign({
    handleShare
  }, {
    ...EXPOSE_FEGO_METHOD.reduce((acc, item) => ({
      ...acc,
      [item.methodName]: () => {
        handleShare(item.value);
      }
    }), {})
  });
};
__sfc_main.components = Object.assign({
  Comp
}, __sfc_main.components);
export default __sfc_main;
</script>

<style lang="less" scoped>
.common-fission-share {
    height: auto !important;
    width: 100%;

    .channels {
        padding: 0.5rem 0;
        .label {
            padding: 0 0.4rem;
            margin-bottom: 0.28rem;
            font-size: 0.36rem;
            color: #262626;
        }
        .list {
            padding: 0 0.4rem;
            margin: 0 0.25rem;
            display: flex;
            overflow-x: auto;
            /* 适用于Webkit浏览器 (Chrome, Safari) */
            -webkit-overflow-scrolling: touch; /* 平滑滚动 */
            /* 适用于Firefox */
            scrollbar-width: none; /* 隐藏滚动条 */
            /* 适用于Internet Explorer 10+ */
            -ms-overflow-style: none; /* 隐藏滚动条 */
            &::-webkit-scrollbar {
                display: none; /* 隐藏滚动条 */
            }
            .item {
                display: inline-block;
                margin: 0 0.4rem;
                &:nth-of-type(1) {
                    margin-left: 0.05rem;
                }
                &:nth-last-of-type(1) {
                    margin-right: 0.05rem;
                }
                .box {
                    display: block;
                    position: relative;
                    padding-bottom: 0.52rem;
                    cursor: pointer;
                }
                .icon {
                    width: 0.84rem;
                    height: 0.84rem;
                    background-size: contain;
                    background-repeat: no-repeat;
                    background-position: center;
                    &.system {
                        background-image: url('../assets/images/system.svg');
                    }
                    &.reddit {
                        background-image: url('../assets/images/reddit.svg');
                    }
                    &.facebook {
                        background-image: url('../assets/images/facebook.svg');
                    }
                    &.telegram {
                        background-image: url('../assets/images/telegram.svg');
                    }
                    &.xiaohongshu {
                        background-image: url('../assets/images/xiaohongshu.svg');
                    }
                    &.twitter {
                        background-image: url('../assets/images/twitter.svg');
                    }
                    &.instagram {
                        background-image: url('../assets/images/instagram.svg');
                    }
                    &.wxFriend {
                        background-image: url('../assets/images/wechat.svg');
                    }
                    &.wxTimeline {
                        background-image: url('../assets/images/wechatMoments.svg');
                    }
                    &.whatsapp {
                        background-image: url('../assets/images/whatsapp.svg');
                    }
                    &.copy {
                        background-image: url('../assets/images/copy.svg');
                    }
                }
                .name {
                    position: absolute;
                    left: 50%;
                    bottom: 0;
                    transform: translateX(-50%);
                    font-size: 0.2rem;
                    color: #0f1112;
                    white-space: nowrap;
                }
            }
        }
    }
}
</style>
